import { ShoppingCart, Truck, ExternalLink, RefreshCw, ClipboardCheck, BookOpen, User, LogOut, ShieldCheck, KeyRound, ShieldAlert, Clock, ArrowUpRight, Scale, Database, Barcode, Building2, FolderKanban } from 'lucide-react';
import { UserProfile } from '../types/auth';

interface HeaderProps {
  lastSyncDisplay: string;
  bcvRate: number;
  bcvStatus?: 'success' | 'stale_cache' | 'fallback';
  bcvUpdatedAt?: string;
  onOpenSupplyModal: () => void;
  onOpenReviewModal: () => void;
  onOpenReceptionModal: () => void;
  onOpenDispatchModal?: () => void;
  onOpenStoreAllocations?: () => void;
  activeAllocationsCount?: number;
  onOpenCommercialHubModal?: () => void;
  onOpenBOMAuditModal?: () => void;
  onOpenAdjustmentModal?: () => void;
  onOpenBarcodeCollectorModal?: () => void;
  scannerQueueCount?: number;
  onOpenKardexModal: () => void;
  onOpenCatalogMaster?: () => void;
  onOpenAuditModal?: () => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
  onRevalidateNotion?: () => void;
  isRevalidatingNotion?: boolean;
  profile?: UserProfile | null;
  onRefreshPermissions?: () => void;
  onChangePin?: () => void;
  onLogout?: () => void;
  isTelegram?: boolean;
  offlineQueueCount?: number;
  onOpenOfflineQueueModal?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  lastSyncDisplay,
  bcvRate,
  bcvStatus = 'success',
  bcvUpdatedAt,
  onOpenSupplyModal,
  onOpenReviewModal,
  onOpenReceptionModal,
  onOpenDispatchModal,
  onOpenStoreAllocations,
  activeAllocationsCount,
  onOpenCommercialHubModal,
  onOpenBOMAuditModal,
  onOpenAdjustmentModal,
  onOpenBarcodeCollectorModal,
  scannerQueueCount,
  onOpenKardexModal,
  onOpenCatalogMaster,
  onOpenAuditModal,
  onRefresh,
  isRefreshing = false,
  onRevalidateNotion,
  isRevalidatingNotion = false,
  profile,
  onRefreshPermissions,
  onChangePin,
  onLogout,
  isTelegram = false,
  offlineQueueCount,
  onOpenOfflineQueueModal
}) => {
  const isSuperadmin = Boolean(profile?.permissions?.includes('Superadmin'));
  const hasPerm = (p: string) => isSuperadmin || Boolean(profile?.permissions?.includes(p as any));

  const canEmitirOAB = hasPerm('Emitir_OAB');
  const canReviewOAB = hasPerm('Revisar_OAB') || hasPerm('Cotizar_OAB') || hasPerm('Aprobar_OAB') || hasPerm('Delegacion_OAB');
  const canRecepcionRampa = hasPerm('Recepcion_Rampa');
  const canDespachoTaller = hasPerm('Despacho_Taller');
  const canAsignacionTiendas = hasPerm('Asignacion_Tiendas');
  const canAuditoriaBOM = hasPerm('Auditoria_BOM');
  const canAjustesStock = hasPerm('Ajustes_Stock');
  const canAuditoriaKardex = hasPerm('Auditoria_Kardex');

  return (
    <header className="bg-surface/95 backdrop-blur border-b border-borderSubtle sticky top-0 z-40 no-print">
      <div className="max-w-[1600px] mx-auto px-4 py-2.5 sm:px-6 flex flex-wrap items-center justify-between gap-3">
        {/* Brand & Title */}
        <div className="flex items-center gap-3">
          <div className="logo-contrast-plate px-2 py-1 rounded-lg flex items-center justify-center">
            <img src="assets/logo-sanesca.png" alt="Sanesca" className="h-6 sm:h-7 w-auto object-contain" onError={(e) => {
              (e.target as HTMLElement).style.display = 'none';
            }} />
            <span className="text-slate-900 font-black text-sm tracking-wider">SANESCA</span>
          </div>
          <div>
            <h1 className="text-white font-bold text-base sm:text-lg tracking-tight flex items-center gap-2">
              📦 Dashboard de Inventario
              <span className="hidden sm:inline-block text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-brand-500/20 text-brand-400 border border-brand-500/40">
                PRO v2.0
              </span>
            </h1>
            <p className="text-xs text-slate-400 hidden md:block">Gerencia de Operaciones · Sanesca Exhibidores</p>
          </div>
        </div>

        {/* Action Hub & Navigation */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
          {/* Main Action: Sugerencias de Abastecimiento */}
          {(!profile || canEmitirOAB) && (
            <button
              onClick={onOpenSupplyModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-brand-500 hover:bg-brand-600 text-slate-950 transition shadow-sm active:scale-95"
              title="Generar Hoja Viajera de Abastecimiento (OAB)"
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              <span>Abastecimiento (OAB)</span>
            </button>
          )}

          {/* Action: Revisión & Transcripción de OAB (Magaly & Compras) */}
          {(!profile || canReviewOAB) && (
            <button
              onClick={onOpenReviewModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-surfaceHigh hover:bg-surfaceHighest text-slate-200 border border-borderSubtle hover:border-amber-500/50 transition active:scale-95"
              title="Puente Papel-Digital: Transcribir vistos buenos de Magaly y cotizaciones de Compras"
            >
              <ClipboardCheck className="w-3.5 h-3.5 text-amber-400" />
              <span>Revisión OAB</span>
            </button>
          )}

          {/* Action: Recepción en Rampa */}
          {(!profile || canRecepcionRampa) && (
            <button
              onClick={onOpenReceptionModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-surfaceHigh hover:bg-surfaceHighest text-slate-200 border border-borderSubtle hover:border-slate-500 transition active:scale-95"
              title="Terminal de Recepción e ingreso de mercancía a almacén"
            >
              <Truck className="w-3.5 h-3.5 text-signal-blue" />
              <span>Recepción en Rampa</span>
            </button>
          )}

          {/* Action: Despacho a Taller (Fase 9A) */}
          {onOpenDispatchModal && (!profile || canDespachoTaller) && (
            <button
              onClick={onOpenDispatchModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 hover:border-rose-500/60 transition active:scale-95 shadow-sm"
              title="Terminal de Despacho físico de materiales a Taller con trazabilidad en 3 niveles"
            >
              <ArrowUpRight className="w-3.5 h-3.5 text-rose-400" />
              <span>Despacho a Taller</span>
            </button>
          )}

          {/* Action: Hub de Clientes, Sucursales y Proyectos de Tienda (PLAN-050 Odoo 18) */}
          {onOpenCommercialHubModal && (
            <button
              onClick={onOpenCommercialHubModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-teal-500/15 hover:bg-teal-500/25 text-teal-300 border border-teal-500/40 hover:border-teal-400 transition active:scale-95 shadow-sm shadow-teal-500/10"
              title="Hub Comercial y de Obras (Odoo 18): Clientes, Sucursales Físicas, Sustratos y Proyectos de Tienda (PLAN-050)"
            >
              <FolderKanban className="w-3.5 h-3.5 text-teal-400" />
              <span>🏗️ Proyectos y Clientes</span>
            </button>
          )}

          {/* Action: Tablero Panorámico de Asignaciones (Fase 10C) */}
          {onOpenStoreAllocations && (!profile || canAsignacionTiendas) && (
            <button
              onClick={onOpenStoreAllocations}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/40 hover:border-amber-400 transition active:scale-95 shadow-sm shadow-amber-500/10"
              title="Tablero Panorámico de Asignaciones y Stock Comprometido por Tienda (Fase 10C)"
            >
              <Building2 className="w-3.5 h-3.5 text-amber-400" />
              <span>🏢 Asignación Tiendas</span>
              {activeAllocationsCount !== undefined && activeAllocationsCount > 0 && (
                <span className="px-1.5 py-0.2 bg-amber-400 text-slate-950 font-bold rounded-full text-[10px]">
                  {activeAllocationsCount}
                </span>
              )}
            </button>
          )}

          {/* Action: Auditoría BOM Ex-Post de Insumos (Fase 9B) */}
          {onOpenBOMAuditModal && (!profile || canAuditoriaBOM) && (
            <button
              onClick={onOpenBOMAuditModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 hover:border-indigo-500/60 transition active:scale-95 shadow-sm"
              title="Auditoría Ex-Post de Insumos: BOM Teórico vs Despachado Real por Tienda/Pedido"
            >
              <Scale className="w-3.5 h-3.5 text-indigo-400" />
              <span>Auditoría BOM</span>
            </button>
          )}

          {/* Action: Conteo Cíclico y Ajustes (Fase 9F) */}
          {onOpenAdjustmentModal && (!profile || canAjustesStock) && (
            <button
              onClick={onOpenAdjustmentModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:border-amber-500/60 transition active:scale-95 shadow-sm"
              title="Conteo Cíclico en Vivo y Ajustes de Inventario (Odoo 18 Quant)"
            >
              <Scale className="w-3.5 h-3.5 text-amber-400" />
              <span>Conteo / Ajuste</span>
            </button>
          )}

          {/* Action: Modo Pistola Zero-Mouse (Fase 9J) */}
          {onOpenBarcodeCollectorModal && (!profile || canAjustesStock) && (
            <button
              onClick={onOpenBarcodeCollectorModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/40 hover:border-emerald-400 transition active:scale-95 shadow-sm shadow-emerald-500/20"
              title="Modo Pistola de Código de Barras 'Zero-Mouse' (Atajo: F2 o Alt+B)"
            >
              <Barcode className="w-3.5 h-3.5 text-emerald-400" />
              <span>📟 Modo Pistola</span>
              {scannerQueueCount !== undefined && scannerQueueCount > 0 && (
                <span className="px-1.5 py-0.2 bg-emerald-400 text-slate-950 font-bold rounded-full text-[10px] animate-pulse">
                  {scannerQueueCount}
                </span>
              )}
            </button>
          )}

          {/* Indicador de Cola Local Offline (Fase 9G.2) */}
          {offlineQueueCount !== undefined && offlineQueueCount > 0 && onOpenOfflineQueueModal && (
            <button
              onClick={onOpenOfflineQueueModal}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold rounded-lg bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 border border-cyan-500/40 hover:border-cyan-400 transition animate-pulse shadow-sm shadow-cyan-500/20"
              title="Existen conteos físicos guardados localmente pendientes de subir al ERP"
            >
              <Database className="w-3.5 h-3.5 text-cyan-400" />
              <span>📡 {offlineQueueCount} Offline</span>
            </button>
          )}

          {/* Action: Libro Mayor Kardex */}
          {(!profile || canAuditoriaKardex) && (
            <button
              onClick={onOpenKardexModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-surfaceHigh hover:bg-surfaceHighest text-slate-200 border border-borderSubtle hover:border-slate-500 transition active:scale-95"
              title="Libro Mayor inmutable de movimientos de almacén"
            >
              <BookOpen className="w-3.5 h-3.5 text-emerald-400" />
              <span className="hidden sm:inline">Libro Mayor</span>
              <span>Kardex</span>
            </button>
          )}

          {/* Action: Catálogo Maestro de Insumos (Fase 11) */}
          {onOpenCatalogMaster && (
            <button
              onClick={onOpenCatalogMaster}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 border border-blue-500/40 hover:border-blue-400 transition active:scale-95 shadow-sm shadow-blue-500/10"
              title="Catálogo Maestro de Insumos, Ficha Técnica, Ontología ISO y Health Checker (Fase 11)"
            >
              <BookOpen className="w-3.5 h-3.5 text-blue-400" />
              <span>📚 Catálogo Insumos</span>
            </button>
          )}

          {/* Action: Bitácora de Auditoría Forense (Exclusivo Superadmin) */}
          {isSuperadmin && onOpenAuditModal && (
            <button
              onClick={onOpenAuditModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 hover:border-rose-500/60 transition active:scale-95 shadow-sm"
              title="Bitácora Forense Integral: Operaciones MTO, Ajustes Físicos y Accesos"
            >
              <ShieldAlert className="w-3.5 h-3.5 text-rose-400" />
              <span className="hidden sm:inline">Auditoría</span>
              <span>Forense</span>
            </button>
          )}

          {/* BCV Rate Badge con Semáforo Tripartito y Tooltip de Resiliencia (PLAN-021) */}
          {bcvRate > 0 && (
            <div 
              className={`hidden lg:flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono rounded-lg bg-surfaceHigh border transition-colors ${
                bcvStatus === 'stale_cache' 
                  ? 'border-amber-500/50 text-amber-300' 
                  : bcvStatus === 'fallback' 
                    ? 'border-rose-500/50 text-rose-300' 
                    : 'border-borderSubtle text-amber-300'
              }`} 
              title={
                bcvStatus === 'stale_cache'
                  ? `Tasa BCV servida desde Caché Edge KV (Última actualización: ${bcvUpdatedAt ? new Date(bcvUpdatedAt).toLocaleString('es-VE') : 'Desconocida'})`
                  : bcvStatus === 'fallback'
                    ? 'Tasa BCV en Modo Contingencia Extrema (Proveedores externos no disponibles)'
                    : `Tasa Oficial BCV en Vivo (${bcvUpdatedAt ? new Date(bcvUpdatedAt).toLocaleDateString('es-VE') : 'Hoy'})`
              }
            >
              <span className={`w-2 h-2 rounded-full inline-block ${
                bcvStatus === 'stale_cache' 
                  ? 'bg-amber-400 animate-pulse' 
                  : bcvStatus === 'fallback' 
                    ? 'bg-rose-500' 
                    : 'bg-emerald-400'
              }`} />
              <span className="font-semibold text-slate-300">BCV:</span>
              <span className="font-bold">Bs {bcvRate.toFixed(2)}</span>
            </div>
          )}

          {/* Cross navigation */}
          <a
            href="https://cazx008.github.io/sanesca-dashboard/"
            target="_blank"
            rel="noreferrer"
            className="hidden xl:inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg text-slate-400 hover:text-slate-200 hover:bg-surfaceHigh transition border border-transparent hover:border-borderSubtle"
          >
            ⚡ Cortes Eléctricos <ExternalLink className="w-3 h-3" />
          </a>
          <a
            href="https://cazx008.github.io/medidas-operativas/"
            target="_blank"
            rel="noreferrer"
            className="hidden xl:inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg text-slate-400 hover:text-slate-200 hover:bg-surfaceHigh transition border border-transparent hover:border-borderSubtle"
          >
            🏭 Medidas Operativas <ExternalLink className="w-3 h-3" />
          </a>

          {/* Sync badge & Refresh */}
          <div className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono rounded-lg bg-surfaceHigh border border-borderSubtle">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <span className="text-slate-300 hidden sm:inline">Sync: {lastSyncDisplay}</span>
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className="text-slate-400 hover:text-white transition ml-1 disabled:opacity-50"
              title="Refrescar inventario"
            >
              <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin text-brand-400' : ''}`} />
            </button>
          </div>

          {/* Botón Revalidar Notion (PLAN-013 - Decisión 3 / Ruta A) */}
          {onRevalidateNotion && (
            <button
              onClick={onRevalidateNotion}
              disabled={isRevalidatingNotion}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/40 hover:border-emerald-400 transition active:scale-95 shadow-sm shadow-emerald-500/10 disabled:opacity-50"
              title="Purga la caché Edge KV y fuerza la consulta en vivo de Notion (detecta cambios en 'Almacenable', existencias y nuevos insumos)"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-emerald-400 ${isRevalidatingNotion ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Revalidar</span> Notion
            </button>
          )}

          {/* Profile Identity Pill & Dedicated Logout */}
          {profile && (
            <div className="flex items-center gap-1.5">
              <div className="flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-xl bg-slate-900 border border-slate-700/80 shadow-inner text-xs">
                <div className="w-6 h-6 rounded-lg bg-gradient-to-tr from-brand-600 to-indigo-600 flex items-center justify-center text-[10px] font-bold text-white shadow-sm">
                  {(profile.name || 'Operador').split(' ').filter(Boolean).map(n => n[0]).slice(0, 2).join('')}
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="font-semibold text-slate-200 text-[11px] truncate max-w-[110px] sm:max-w-[150px]">
                    {profile.name || 'Operador'}
                  </span>
                  <span className="text-[9px] font-mono text-slate-400 truncate max-w-[110px]">
                    {profile.permissions.includes('Superadmin') ? (
                      <span className="text-rose-400 font-bold">★ Superadmin</span>
                    ) : profile.isTemporary || profile.authMethod === '2fa_temp' ? (
                      <span className="text-amber-400 font-bold flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" /> 1h Temp
                      </span>
                    ) : (
                      profile.puestos?.[0] || 'Personal'
                    )}
                  </span>
                </div>

                {onRefreshPermissions && (
                  <button
                    type="button"
                    onClick={onRefreshPermissions}
                    className="p-1 text-slate-400 hover:text-emerald-400 transition-colors ml-0.5"
                    title="Refrescar mis permisos desde Notion"
                  >
                    <RefreshCw className="w-3 h-3" />
                  </button>
                )}

                {onChangePin && (
                  <button
                    type="button"
                    onClick={onChangePin}
                    className="p-1 text-slate-400 hover:text-amber-400 transition-colors"
                    title="Modificar mi PIN de acceso en Notion"
                  >
                    <KeyRound className="w-3 h-3" />
                  </button>
                )}
              </div>

              {!isTelegram && onLogout && (
                <button
                  type="button"
                  onClick={onLogout}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 hover:border-rose-500/60 transition active:scale-95 shadow-sm"
                  title="Cerrar sesión en este equipo compartido"
                >
                  <LogOut className="w-3.5 h-3.5 text-rose-400" />
                  <span className="hidden sm:inline">Salir</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
